import { useQuery } from '@apollo/client';
import find from 'lodash/find';
import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PageLoading } from '@pv/common/components';
import { useUserAuth } from '../providers/auth';
import { useVenues } from './venues';
import { contractFields, eventFields, eventQuery } from './graphql';
import { Event2Query } from '@pv/common/graphql/operations';
export { contractFields, eventFields };

interface EventContextProps {
  event: Event2Query['event'];
  loading: boolean;
  eventVenue: any;
  refetchEvent: () => void;
}
const EventContext = createContext<EventContextProps>({
  event: null,
  loading: false,
  eventVenue: null,
  refetchEvent: () => {},
});

export const useEvent = () => useContext(EventContext);

const EventProvider = ({
  children,
  eventId = '',
}: {
  children: React.ReactNode;
  eventId: string;
}) => {
  const params = useParams();
  const { venues } = useVenues();
  const { user } = useUserAuth();
  const { id: routeId } = params;
  // The route carries the numeric id the app links by, or the event's slug
  // (message_token), which is the id the partner API hands out; the query
  // resolves either.
  const id = routeId || eventId;
  const skip = !id || !venues || !user;
  const variables = { id };

  const {
    data,
    refetch: refetchEvent,
    loading,
  } = useQuery<Event2Query>(eventQuery, {
    variables,
    skip,
  });
  const event = data?.event;
  const eventVenue = find(venues, (v) => v.id === event?.venue?.id);

  const memoizedValue = useMemo(
    () => ({
      event: event,
      loading,
      refetchEvent,
      eventVenue,
    }),
    [event, loading, eventVenue, refetchEvent]
  );

  return (
    <EventContext.Provider value={memoizedValue}>
      {loading || !venues || !user ? <PageLoading /> : children}
    </EventContext.Provider>
  );
};

export default EventProvider;
